import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Building2,
  Users,
  BarChart3,
  TrendingUp,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle,
  Shield,
  Activity,
  KeyRound,
  Pencil,
  MoreVertical,
  Ban,
  Trash2,
  RefreshCw,
  AlertTriangle,
  FileText,
  Loader2,
  Globe,
  Facebook,
  Instagram,
  Linkedin,
  Trophy,
  Gauge,
  Bot
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiService } from '@/services/api'
import { CanalProConfigDrawer } from '@/features/tenants/components/CanalProConfigDrawer'
import { TenantDetail, TenantUser, TenantStats, TenantTokenStatus, TenantTokenStatusItem } from '@/types'
import { toast } from 'sonner'
import { ChangePasswordDialog } from './components/ChangePasswordDialog'
import { TenantContractsTab } from './components/TenantContractsTab'
import { TenantManagementDialog, TenantFormValues } from './components/TenantManagementDialog'

type TabType = 'overview' | 'users' | 'stats' | 'contracts' | 'modules'



export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [canalproDrawerOpen, setCanalproDrawerOpen] = useState(false)
  const [tenant, setTenant] = useState<TenantDetail | null>(null)
  const [users, setUsers] = useState<TenantUser[]>([])
  const [stats, setStats] = useState<TenantStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [tokenStatus, setTokenStatus] = useState<TenantTokenStatus | null>(null)
  const [tokenStatusLoading, setTokenStatusLoading] = useState(true)

  // Dialog States
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [submittingEdit, setSubmittingEdit] = useState(false)

  const handleToggleStatus = async () => {
    if (!tenant) return

    const action = tenant.is_active ? 'desativar' : 'ativar'
    if (!window.confirm(`Tem certeza que deseja ${action} este tenant?`)) return

    try {
      await apiService.updateTenantStatus(parseInt(id!), !tenant.is_active)
      toast.success(`Tenant ${action}do com sucesso!`)
      loadTenantData()
    } catch (error) {
      toast.error(`Erro ao ${action} tenant`)
    }
  }

  const handleDeleteTenant = async () => {
    if (!tenant) return

    const confirmText = prompt(`ATENÇÃO: Ação irreversível!\n\nPara confirmar a exclusão do tenant "${tenant.name}", digite DELETAR abaixo:`)
    if (confirmText !== 'DELETAR') return

    try {
      await apiService.deleteTenant(parseInt(id!))
      toast.success('Tenant deletado com sucesso')
      navigate('/tenants')
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || 'Erro ao deletar tenant')
    }
  }

  const handleEditSubmit = async (values: TenantFormValues) => {
    if (!id) return
    try {
      setSubmittingEdit(true)
      await apiService.updateTenant(parseInt(id), values)
      toast.success('Tenant atualizado com sucesso!')
      setEditDialogOpen(false)
      loadTenantData()
    } catch (error) {
      console.error(error)
      toast.error('Erro ao atualizar tenant')
    } finally {
      setSubmittingEdit(false)
    }
  }

  const handleRefresh = () => {
    loadTenantData()
    loadTokenStatus()
    if (activeTab === 'users') loadUsers()
    if (activeTab === 'stats') loadStats()
    toast.success('Dados atualizados!')
  }

  useEffect(() => {
    loadTenantData()
    loadTokenStatus()
  }, [id])

  const loadTenantData = async () => {
    if (!id) return

    try {
      setLoading(true)
      const tenantData = await apiService.getTenantDetail(parseInt(id))
      setTenant(tenantData)
    } catch (error) {
      console.error('Erro ao carregar tenant:', error)
      toast.error('Erro ao carregar detalhes do tenant')
    } finally {
      setLoading(false)
    }
  }

  const loadTokenStatus = async () => {
    if (!id) return

    try {
      setTokenStatusLoading(true)
      const status = await apiService.getTenantTokenStatus(parseInt(id))
      setTokenStatus(status)
    } catch (error) {
      console.error('Erro ao carregar status do token:', error)
      setTokenStatus(null)
      toast.error('Erro ao carregar status do token CanalPro')
    } finally {
      setTokenStatusLoading(false)
    }
  }

  const loadUsers = async () => {
    if (!id) return

    try {
      const response = await apiService.getTenantUsers(parseInt(id))
      setUsers(response.users)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      toast.error('Erro ao carregar usuários')
    }
  }

  const handleDeleteUser = async (userId: number, username: string) => {
    // Confirmação dupla para evitar acidentes
    const confirmText = prompt(`Para confirmar a exclusão do usuário "${username}", digite DELETAR abaixo:`)
    if (confirmText !== 'DELETAR') return

    try {
      await apiService.deleteUser(userId)
      toast.success('Usuário deletado com sucesso')
      loadUsers() // Recarrega a lista
    } catch (error: any) {
      console.error('Erro ao deletar usuário:', error)
      toast.error(error.message || 'Erro ao deletar usuário')
    }
  }

  const loadStats = async () => {
    if (!id) return

    try {
      const statsData = await apiService.getTenantStats(parseInt(id))
      setStats(statsData)
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error)
      toast.error('Erro ao carregar estatísticas')
    }
  }

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      loadUsers()
    }
    if (activeTab === 'stats' && !stats) {
      loadStats()
    }
  }, [activeTab])

  if (loading || !tenant) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando detalhes do tenant...</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Informações Gerais', icon: Building2 },
    { id: 'users' as TabType, label: 'Usuários', icon: Users },
    { id: 'stats' as TabType, label: 'Estatísticas', icon: BarChart3 },
    { id: 'contracts' as TabType, label: 'Contratos', icon: FileText },
    { id: 'modules' as TabType, label: 'Módulos Extras', icon: Trophy },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenants')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
            <p className="text-sm text-gray-500">
              {tenant.tenant_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'} •
              ID: {tenant.tenant_id}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${tenant.is_active
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
            }`}>
            {tenant.is_active ? 'Ativo' : 'Inativo'}
          </span>

          <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>

          <Button variant="outline" size="sm" className="gap-2" onClick={() => setEditDialogOpen(true)}>
            <Pencil className="w-4 h-4" />
            Editar
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleToggleStatus}>
                {tenant.is_active ? (
                  <>
                    <Ban className="w-4 h-4 mr-2" />
                    Desativar Tenant
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Ativar Tenant
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-red-600" onClick={handleDeleteTenant}>
                <Trash2 className="w-4 h-4 mr-2" />
                Deletar Tenant
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <nav className="flex gap-8 px-1 min-w-max">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-4 px-1 border-b-2 transition-colors whitespace-nowrap ${isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.id === 'modules' ? 'Módulos & Marketplace' : tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {activeTab === 'overview' && (
          <OverviewTab
            tenant={tenant}
            tokenStatus={tokenStatus}
            tokenLoading={tokenStatusLoading}
          />
        )}
        {activeTab === 'users' && <UsersTab users={users} loading={!users} onDelete={handleDeleteUser} />}
        {activeTab === 'stats' && <StatsTab stats={stats} loading={!stats} />}
        {activeTab === 'contracts' && <TenantContractsTab tenantId={parseInt(id!)} />}
        {activeTab === 'modules' && <ModulesTab tenant={tenant} onUpdate={loadTenantData} onConfigureCanalpro={() => setCanalproDrawerOpen(true)} />}

        {tenant && (
          <CanalProConfigDrawer
            tenant={tenant}
            open={canalproDrawerOpen}
            onOpenChange={setCanalproDrawerOpen}
            onSaved={loadTenantData}
          />
        )}
      </div>

      <TenantManagementDialog
        tenant={tenant}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleEditSubmit}
        submitting={submittingEdit}
      />
    </div>
  )
}

function OverviewTab({
  tenant,
  tokenStatus,
  tokenLoading,
}: {
  tenant: TenantDetail
  tokenStatus: TenantTokenStatus | null
  tokenLoading: boolean
}) {
  const isPJ = tenant.tenant_type === 'PJ'

  const infoItems = [
    {
      icon: Building2,
      label: isPJ ? 'Razão Social' : 'Nome Completo',
      value: isPJ ? (tenant.company_name || '—') : (tenant.full_name || '—')
    },
    {
      icon: FileText, // TODO: Use better icon if available
      label: isPJ ? 'CNPJ' : 'CPF',
      value: isPJ ? (tenant.cnpj || '—') : (tenant.cpf || '—')
    },
    {
      icon: Mail,
      label: 'Email',
      value: tenant.email || 'Não informado'
    },
    {
      icon: Phone,
      label: 'Telefone',
      value: tenant.phone || 'Não informado'
    },
    {
      icon: Calendar,
      label: 'Data de Criação',
      value: tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('pt-BR') : 'Não informado'
    },
    ...(isPJ ? [{
      icon: FileText,
      label: 'Inscrição Estadual / CRECI',
      value: tenant.creci || '—'
    }] : [])
  ]

  const statusInfoMap: Record<
    TenantTokenStatusItem['status'] | 'unknown',
    { label: string; badge: string; description: string }
  > = {
    active: {
      label: 'Token ativo',
      badge: 'bg-green-100 text-green-700',
      description: 'Token válido e monitoramento automático funcionando.',
    },
    expiring_soon: {
      label: 'Expirando em breve',
      badge: 'bg-yellow-100 text-yellow-700',
      description: 'Token válido, mas expira nas próximas horas. Avalie a renovação.',
    },
    expired: {
      label: 'Token expirado',
      badge: 'bg-red-100 text-red-700',
      description: 'Token expirado. A integração CanalPro está inativa até uma nova renovação.',
    },
    unknown: {
      label: 'Status indisponível',
      badge: 'bg-gray-100 text-gray-600',
      description: 'Não foi possível determinar o status atual do token.',
    },
  }

  const getStatusInfo = (status?: TenantTokenStatusItem['status']) =>
    statusInfoMap[status ?? 'unknown']

  const formatDateTime = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  }

  const formatRelativeTime = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null

    const diffMs = date.getTime() - Date.now()
    const diffMinutes = Math.round(diffMs / 60000)
    if (!Number.isFinite(diffMinutes)) return null

    const absMinutes = Math.abs(diffMinutes)

    if (absMinutes >= 1440) {
      const days = Math.round(diffMinutes / 1440)
      return days >= 0 ? `em ${days} dia(s)` : `há ${Math.abs(days)} dia(s)`
    }

    if (absMinutes >= 60) {
      const hours = Math.round(diffMinutes / 60)
      return hours >= 0 ? `em ${hours} hora(s)` : `há ${Math.abs(hours)} hora(s)`
    }

    return diffMinutes >= 0 ? `em ${diffMinutes} minuto(s)` : `há ${Math.abs(diffMinutes)} minuto(s)`
  }

  const canalProToken = tokenStatus?.tokens.find((token) =>
    token.provider?.toLowerCase() === 'gandalf' || token.provider?.toLowerCase() === 'canalpro'
  ) ?? tokenStatus?.tokens[0]

  const statusInfo = getStatusInfo(canalProToken?.status)
  const expiresAtLabel = formatDateTime(canalProToken?.expires_at)
  const expiresAtRelative = formatRelativeTime(canalProToken?.expires_at)

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{tenant.users_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Propriedades</p>
              <p className="text-2xl font-bold text-gray-900">{tenant.properties_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-2xl font-bold text-gray-900">
                {tenant.is_active ? 'Ativo' : 'Inativo'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Token Monitor Status */}
      <div className="bg-white border border-indigo-200 rounded-lg p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <KeyRound className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-indigo-700">Token CanalPro</p>
              <p className="text-xs text-indigo-500">Monitoramento em tempo real da integração CanalPro/Gandalf</p>
            </div>
          </div>
          {tokenLoading ? (
            <div className="flex items-center gap-2 text-indigo-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Carregando status...</span>
            </div>
          ) : (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.badge}`}>
              {statusInfo.label}
            </span>
          )}
        </div>

        <div className="mt-4">
          {tokenLoading ? (
            <div className="h-24 flex items-center justify-center text-sm text-gray-500">
              Buscando últimas informações...
            </div>
          ) : canalProToken ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Situação atual</p>
                <p className="mt-2 text-sm text-gray-700 leading-snug">{statusInfo.description}</p>
                {tokenStatus?.summary?.last_check && (
                  <p className="mt-3 text-xs text-gray-500">
                    Última verificação: {formatDateTime(tokenStatus.summary.last_check)}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Expira em</p>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {expiresAtLabel || 'Sem data registrada'}
                </p>
                {expiresAtRelative && (
                  <p className="text-xs text-gray-500 mt-1">{expiresAtRelative}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Automação</p>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {canalProToken.automation_enabled ? 'Renovação automática ativa' : 'Renovação manual'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Total de tokens: {tokenStatus?.summary?.total ?? 0} • Expirando: {tokenStatus?.summary?.expiring_soon ?? 0}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              Nenhum token CanalPro encontrado para este tenant.
            </div>
          )}
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-2">Health Score</p>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-5xl font-bold text-purple-600">
                {tenant.health_score || '—'}
              </span>
              {tenant.health_score && (
                <span className="text-lg text-gray-500">/100</span>
              )}
            </div>
            {tenant.health_score && (
              <div className="mt-3">
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${tenant.health_score >= 80 ? 'bg-green-500' :
                      tenant.health_score >= 60 ? 'bg-yellow-500' :
                        tenant.health_score >= 40 ? 'bg-orange-500' :
                          'bg-red-500'
                      }`}
                    style={{ width: `${tenant.health_score}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>
            )}
          </div>
          <Activity className="w-10 h-10 text-purple-400" />
        </div>

        {/* Alertas baseados no health score */}
        {tenant.health_score && tenant.health_score < 50 && (
          <div className="mt-4 p-3 bg-red-50 rounded-lg flex items-start gap-3 border border-red-200">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Atenção Necessária</p>
              <p className="text-xs text-red-600 mt-1">
                Este tenant apresenta baixo engajamento. Considere entrar em contato.
              </p>
            </div>
          </div>
        )}

        {!tenant.health_score && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-3 border border-blue-200">
            <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-blue-600">
                Health Score será calculado após atividade do tenant.
              </p>
            </div>
          </div>
        )}

        {tenant.properties_count === 0 && tenant.is_active && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg flex items-start gap-3 border border-yellow-200">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Nenhum Imóvel Cadastrado</p>
              <p className="text-xs text-yellow-600 mt-1">
                Este tenant está ativo mas ainda não cadastrou nenhum imóvel.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Cadastral Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Cadastrais</h3>
          <div className="bg-gray-50 rounded-lg divide-y divide-gray-100 border border-gray-100">
            {infoItems.map((item, index) => {
              const Icon = item.icon
              return (
                <div key={index} className="flex items-center gap-3 p-4">
                  <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">{item.label}</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{item.value}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Address */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Localização Principal</h3>
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-100 h-[calc(100%-2.5rem)]">
            <div className="flex items-start gap-4 h-full">
              <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500 mb-1">Endereço Completo</p>
                {tenant.address ? (
                  <div className="space-y-1">
                    <p className="text-base text-gray-900 font-medium leading-snug">
                      {tenant.street ? `${tenant.street}, ${tenant.number}` : tenant.address}
                    </p>
                    {(tenant.neighborhood || tenant.complement) && (
                      <p className="text-sm text-gray-600">
                        {[tenant.neighborhood, tenant.complement].filter(Boolean).join(' • ')}
                      </p>
                    )}
                    {(tenant.city || tenant.state || tenant.zip_code) && (
                      <p className="text-sm text-gray-600">
                        {[tenant.city, tenant.state].filter(Boolean).join(' / ')}
                        {tenant.zip_code && ` • CEP ${tenant.zip_code}`}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Endereço não cadastrado.</p>
                )}

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-3">Redes Sociais & Web</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 rounded-full" disabled>
                      <Globe className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 rounded-full" disabled>
                      <Instagram className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 rounded-full" disabled>
                      <Facebook className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 rounded-full" disabled>
                      <Linkedin className="w-4 h-4 text-gray-400" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Information */}
      {tenant.contract && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contrato CanalPro</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-600 mb-1">Máx. Propriedades</p>
              <p className="text-2xl font-bold text-gray-900">{tenant.contract.max_properties ?? '—'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-600 mb-1">Máx. Destaques</p>
              <p className="text-2xl font-bold text-gray-900">{tenant.contract.max_highlights ?? '—'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-600 mb-1">Máx. Super Destaques</p>
              <p className="text-2xl font-bold text-gray-900">{tenant.contract.max_super_highlights ?? '—'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function UsersTab({ users, loading, onDelete }: { users: TenantUser[]; loading: boolean; onDelete: (id: number, username: string) => void }) {
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null)

  const handleChangePassword = (user: TenantUser) => {
    setSelectedUser(user)
    setPasswordDialogOpen(true)
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Carregando usuários...</p>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Nenhum usuário encontrado</p>
        <p className="text-sm text-gray-500 mt-2">Os usuários cadastrados aparecerão aqui.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Usuários ({users.length})
        </h3>
      </div>

      <div className="overflow-hidden border rounded-lg">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome Completo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CPF
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CRECI
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cargo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cidade/UF
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Criado em
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Último acesso
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 h-9 w-9 bg-blue-100 rounded-full flex items-center justify-center border border-blue-200">
                      <span className="text-sm font-bold text-blue-600">
                        {user.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium text-gray-900">{user.username}</span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.email}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.full_name || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.cpf || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.creci ? `${user.creci}${user.creci_state ? `/${user.creci_state}` : ''}` : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.primary_role || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                  {user.city ? `${user.city}${user.state ? `/${user.state}` : ''}` : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {user.is_admin ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                      Usuário
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {user.is_active ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      Inativo
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString('pt-BR') : 'Nunca'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleChangePassword(user)}
                    className="gap-2 h-8"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    Senha
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(user.id, user.username)}
                    className="gap-2 h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Excluir
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog de troca de senha */}
      {selectedUser && (
        <ChangePasswordDialog
          open={passwordDialogOpen}
          onOpenChange={setPasswordDialogOpen}
          userId={selectedUser.id}
          userName={selectedUser.username}
        />
      )}
    </div>
  )
}

function StatsTab({ stats, loading }: { stats: TenantStats | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Carregando estatísticas...</p>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Estatísticas não disponíveis</p>
      </div>
    )
  }

  const propertyStats = stats.stats.properties


  // Transformando o objeto properties_by_status em array se necessário
  type StatusCount = { status: string; count: number }
  let statusChartData: StatusCount[] = []

  if (Array.isArray(propertyStats.by_status)) {
    // Se já vier array (depende da implementação do backend)
    statusChartData = propertyStats.by_status.map((item: any) => ({ status: item.status || item.key, count: item.count || item.value }))
  } else if (typeof propertyStats.by_status === 'object') {
    statusChartData = Object.entries(propertyStats.by_status).map(([key, value]) => ({ status: key, count: value as number }))
  }


  return (
    <div className="space-y-6">
      {/* User Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas de Usuários</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-md">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-blue-900">Total de Usuários</p>
            </div>
            <p className="text-3xl font-bold text-blue-700">{stats.stats.users.total}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-5 border border-purple-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-md">
                <Shield className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-purple-900">Administradores</p>
            </div>
            <p className="text-3xl font-bold text-purple-700">{stats.stats.users.admins}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-5 border border-green-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-md">
                <Users className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-sm font-medium text-green-900">Usuários Regulares</p>
            </div>
            <p className="text-3xl font-bold text-green-700">{stats.stats.users.regular}</p>
          </div>
        </div>
      </div>

      {/* Property Stats */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfólio de Imóveis</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Total de Imóveis</p>
            <p className="text-4xl font-extrabold text-gray-900">{stats.stats.properties.total}</p>
          </div>

          <div className="space-y-3">
            {statusChartData.slice(0, 5).map((item) => (
              <div key={item.status} className="flex items-center gap-3">
                <span className="text-sm font-medium w-32 truncate capitalize text-gray-700">
                  {item.status.toLowerCase().replace(/_/g, ' ')}
                </span>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${(item.count / stats.stats.properties.total) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-gray-900 min-w-[30px] text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MODULES TAB (PREMIUM UI)
// ============================================================================

interface ModuleConfig {
  id: string
  key: string // chave no objeto modules do banco
  title: string
  description: string
  icon: React.ReactNode
  color: string // classes de cor (bg/text)
  status: 'available' | 'coming_soon' | 'beta'
}

// Portais que têm aba "Portais" dedicada — o card no Marketplace mostra resumo + link.
const PORTAL_MODULE_KEYS = new Set(['canalpro_enabled', 'portal_62imoveis_enabled'])

const PORTAL_MODE_SUMMARY: Record<string, { imoveisKey: string; leadsKey: string; defaults: { imoveis: string; leads: string }; labels: { imoveis: Record<string, string>; leads: Record<string, string> } }> = {
  canalpro_enabled: {
    imoveisKey: 'canalpro_imoveis_mode',
    leadsKey: 'canalpro_leads_mode',
    defaults: { imoveis: 'xml', leads: 'webhook' },
    labels: {
      imoveis: { xml: 'XML', graphql: 'GraphQL' },
      leads: { webhook: 'Webhook', graphql: 'GraphQL' },
    },
  },
  portal_62imoveis_enabled: {
    imoveisKey: 'portal_62imoveis_imoveis_mode',
    leadsKey: 'portal_62imoveis_leads_mode',
    defaults: { imoveis: 'xml', leads: 'webhook' },
    labels: {
      imoveis: { xml: 'XML', graphql: 'GraphQL' },
      leads: { webhook: 'Webhook', graphql: 'GraphQL' },
    },
  },
}

function formatPortalModeSummary(moduleKey: string, caps: Record<string, any>): string | null {
  const cfg = PORTAL_MODE_SUMMARY[moduleKey]
  if (!cfg) return null
  const imv = caps[cfg.imoveisKey] ?? cfg.defaults.imoveis
  const lds = caps[cfg.leadsKey] ?? cfg.defaults.leads
  const imvLabel = cfg.labels.imoveis[imv] ?? imv
  const ldsLabel = cfg.labels.leads[lds] ?? lds
  return `Modo: ${imvLabel} (imóveis) + ${ldsLabel} (leads)`
}

function ModulesTab({ tenant, onUpdate, onConfigureCanalpro }: { tenant: any, onUpdate: () => void, onConfigureCanalpro?: () => void }) {
  const [modulesState, setModulesState] = useState({
    gamification_enabled: tenant.gamification_enabled || tenant.capabilities_override?.gamification === 'full' || false,
    site_enabled: tenant.capabilities_override?.site_enabled || false,
    active_offer_enabled: tenant.capabilities_override?.active_offer_enabled || tenant.capabilities_override?.active_offer || false,
    cockpit_enabled: tenant.capabilities_override?.cockpit_enabled || false,
    lancamentos_enabled: tenant.capabilities_override?.lancamentos_enabled || false,
    parcerias_enabled: tenant.capabilities_override?.parcerias_enabled || false,
    canalpro_enabled: tenant.capabilities_override?.canalpro_enabled || false,
    portal_proprietario_enabled: tenant.capabilities_override?.portal_proprietario_enabled || false,
    // Onda 7 (PLANO_MIGRACAO_COMPLETA_V2): toggle Agentes IA.
    // Default TRUE para tenants existentes (grandfathering); novos podem entrar FALSE.
    ai_agents_enabled: tenant.capabilities_override?.ai_agents_enabled !== false,
    portal_62imoveis_enabled: tenant.capabilities_override?.portal_62imoveis_enabled || false,
    aluguel_enabled: tenant.capabilities_override?.aluguel_enabled || false,
    network_tiers_enabled: tenant.capabilities_override?.network_tiers_enabled || false,
    mineracao_enabled: tenant.capabilities_override?.mineracao_enabled || false
  })
  const [saving, setSaving] = useState(false)

  // Detectar mudanças para mostrar botão de salvar flutuante ou no topo
  const initialGamification = tenant.gamification_enabled || tenant.capabilities_override?.gamification === 'full' || false
  const initialSite = tenant.capabilities_override?.site_enabled || false
  const initialActiveOffer = tenant.capabilities_override?.active_offer_enabled || tenant.capabilities_override?.active_offer || false
  const initialCockpit = tenant.capabilities_override?.cockpit_enabled || false
  const initialLancamentos = tenant.capabilities_override?.lancamentos_enabled || false
  const initialParcerias = tenant.capabilities_override?.parcerias_enabled || false
  const initialCanalpro = tenant.capabilities_override?.canalpro_enabled || false
  const initialPortalProprietario = tenant.capabilities_override?.portal_proprietario_enabled || false
  // Onda 7: AI Agents — default TRUE para grandfathering (apenas FALSE explícito desabilita)
  const initialAiAgents = tenant.capabilities_override?.ai_agents_enabled !== false
  const initialPortal62imoveis = tenant.capabilities_override?.portal_62imoveis_enabled || false
  const initialAluguel = tenant.capabilities_override?.aluguel_enabled || false
  const initialNetworkTiers = tenant.capabilities_override?.network_tiers_enabled || false
  const initialMineracao = tenant.capabilities_override?.mineracao_enabled || false

  const hasChanges =
    modulesState.gamification_enabled !== initialGamification ||
    modulesState.site_enabled !== initialSite ||
    modulesState.active_offer_enabled !== initialActiveOffer ||
    modulesState.cockpit_enabled !== initialCockpit ||
    modulesState.lancamentos_enabled !== initialLancamentos ||
    modulesState.parcerias_enabled !== initialParcerias ||
    modulesState.canalpro_enabled !== initialCanalpro ||
    modulesState.portal_proprietario_enabled !== initialPortalProprietario ||
    modulesState.ai_agents_enabled !== initialAiAgents ||
    modulesState.portal_62imoveis_enabled !== initialPortal62imoveis ||
    modulesState.aluguel_enabled !== initialAluguel ||
    modulesState.network_tiers_enabled !== initialNetworkTiers ||
    modulesState.mineracao_enabled !== initialMineracao

  const handleSave = async () => {
    try {
      setSaving(true)
      await apiService.updateTenantModules(parseInt(tenant.id), modulesState)
      toast.success('Módulos atualizados com sucesso!')
      onUpdate()
    } catch (error) {
      toast.error('Erro ao atualizar módulos')
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  const modulesList: ModuleConfig[] = [
    {
      id: 'gamification',
      key: 'gamification_enabled',
      title: 'Gamificação & Engajamento',
      description: 'Sistema completo de níveis, badges, rankings e desafios para motivar a equipe de corretores.',
      icon: <Trophy className="w-6 h-6" />,
      color: 'text-amber-500 bg-amber-50 border-amber-200',
      status: 'available'
    },
    {
      id: 'site_engine',
      key: 'site_enabled',
      title: 'Site White-Label',
      description: 'Website imobiliário exclusivo e personalizável com busca avançada e SEO otimizado.',
      icon: <Globe className="w-6 h-6" />,
      color: 'text-blue-500 bg-blue-50 border-blue-200',
      status: 'available'
    },
    {
      id: 'active_offer',
      key: 'active_offer_enabled',
      title: 'Oferta Ativa & Playbooks',
      description: 'Módulo completo de discagem, gestão de scripts de vendas (Playbooks) e filas inteligentes de leads.',
      icon: <Phone className="w-6 h-6" />,
      color: 'text-green-500 bg-green-50 border-green-200',
      status: 'available'
    },
    {
      id: 'cockpit',
      key: 'cockpit_enabled',
      title: 'Cockpit de Equipes',
      description: 'Painel gerencial para acompanhar desempenho de equipes e corretores em tempo real.',
      icon: <Gauge className="w-6 h-6" />,
      color: 'text-cyan-500 bg-cyan-50 border-cyan-200',
      status: 'available'
    },
    {
      id: 'lancamentos',
      key: 'lancamentos_enabled',
      title: 'Lançamentos Imobiliários',
      description: 'Gestão completa de empreendimentos, tabelas de preço e controle de unidades.',
      icon: <Building2 className="w-6 h-6" />,
      color: 'text-rose-500 bg-rose-50 border-rose-200',
      status: 'available'
    },
    {
      id: 'parcerias',
      key: 'parcerias_enabled',
      title: 'Parcerias',
      description: 'Sistema de parcerias entre corretores e imobiliárias para compartilhamento de imóveis.',
      icon: <Users className="w-6 h-6" />,
      color: 'text-violet-500 bg-violet-50 border-violet-200',
      status: 'available'
    },
    {
      id: 'canalpro',
      key: 'canalpro_enabled',
      title: 'Integração CanalPro',
      description: 'Sincronização de imóveis com ZAP, Vivareal e OLX via API CanalPro. Inclui Refresh automático de anúncios.',
      icon: <Globe className="w-6 h-6" />,
      color: 'text-emerald-500 bg-emerald-50 border-emerald-200',
      status: 'available'
    },
    {
      id: 'portal_proprietario',
      key: 'portal_proprietario_enabled',
      title: 'Portal do Proprietário',
      description: 'Portal exclusivo para proprietários: autorizações de venda digital, assinatura eletrônica, acompanhamento de imóveis e contratos.',
      icon: <FileText className="w-6 h-6" />,
      color: 'text-orange-500 bg-orange-50 border-orange-200',
      status: 'available'
    },
    {
      // Onda 7 (PLANO_MIGRACAO_COMPLETA_V2)
      id: 'ai_agents',
      key: 'ai_agents_enabled',
      title: 'Agentes IA',
      description: 'Plataforma de agentes autônomos: SDR Inbound/Outbound/Lançamento via WhatsApp + Genesis Copilot (assistente interno do CRM). Tenant cria agentes customizados via UI usando BYOK.',
      icon: <Bot className="w-6 h-6" />,
      color: 'text-purple-500 bg-purple-50 border-purple-200',
      status: 'available'
    },
    {
      id: 'portal_62imoveis',
      key: 'portal_62imoveis_enabled',
      title: 'Portal 62imoveis',
      description: 'Integração com o portal 62imoveis: feed XML de imóveis e recebimento de leads via webhook.',
      icon: <Building2 className="w-6 h-6" />,
      color: 'text-blue-500 bg-blue-50 border-blue-200',
      status: 'available'
    },
    {
      id: 'aluguel',
      key: 'aluguel_enabled',
      title: 'Aluguel',
      description: 'Gestão de locação de imóveis: contratos de aluguel, controle de inquilinos e ciclo de locação.',
      icon: <KeyRound className="w-6 h-6" />,
      color: 'text-teal-500 bg-teal-50 border-teal-200',
      status: 'available'
    },
    {
      id: 'network_tiers',
      key: 'network_tiers_enabled',
      title: 'Graduação de Rede',
      description: 'Níveis de comissão por desempenho dos parceiros da rede.',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'text-emerald-500 bg-emerald-50 border-emerald-200',
      status: 'available'
    },
    {
      id: 'mineracao',
      key: 'mineracao_enabled',
      title: 'Captação (Mineração de Imóveis)',
      description: 'Descoberta de imóveis e proprietários na base territorial para prospecção de captação.',
      icon: <MapPin className="w-6 h-6" />,
      color: 'text-amber-500 bg-amber-50 border-amber-200',
      status: 'available'
    },
    {
      id: 'crm_pro',
      key: 'crm_pro_enabled',
      title: 'CRM Pro + Funis',
      description: 'Automação de vendas, múltiplos funis de kanban e relatórios avançados de conversão.',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'text-indigo-500 bg-indigo-50 border-indigo-200',
      status: 'coming_soon'
    },
    {
      id: 'whatsapp_api',
      key: 'whatsapp_enabled',
      title: 'WhatsApp Business API',
      description: 'Integração oficial para disparo de mensagens e chatbots de atendimento.',
      icon: <Phone className="w-6 h-6" />,
      color: 'text-green-500 bg-green-50 border-green-200',
      status: 'coming_soon'
    }
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-gray-500" />
            Marketplace de Módulos
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie as funcionalidades adicionais ativas para este tenant.
          </p>
        </div>

        {hasChanges && (
          <div className="flex items-center gap-4 animate-in fade-in slide-in-from-right-4 duration-300">
            <span className="text-sm text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
              Alterações pendentes
            </span>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all hover:shadow-lg"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Salvar Alterações
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {modulesList.map((module) => {
          const isEnabled = modulesState[module.key as keyof typeof modulesState]
          const isAvailable = module.status === 'available'

          return (
            <div
              key={module.id}
              className={`
                                relative group rounded-2xl border transition-all duration-300
                                ${isEnabled
                  ? 'bg-white border-blue-200 shadow-sm ring-1 ring-blue-100'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                }
                                ${!isAvailable && 'opacity-70 grayscale-[0.5]'}
                            `}
            >
              {/* Card Header */}
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-xl ${module.color} transition-transform group-hover:scale-110 duration-300`}>
                    {module.icon}
                  </div>

                  {isAvailable ? (
                    <button
                      onClick={() => setModulesState(prev => ({
                        ...prev,
                        [module.key]: !prev[module.key as keyof typeof prev]
                      }))}
                      className={`
                                                relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                                ${isEnabled ? 'bg-blue-600' : 'bg-gray-200'}
                                            `}
                    >
                      <span
                        className={`
                                                    inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm
                                                    ${isEnabled ? 'translate-x-6' : 'translate-x-1'}
                                                `}
                      />
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Em breve
                    </span>
                  )}
                </div>

                <h4 className="text-base font-bold text-gray-900 mb-2">
                  {module.title}
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed min-h-[60px]">
                  {module.description}
                </p>

                {/* Resumo do modo (só pra portais com aba Portais) */}
                {isEnabled && PORTAL_MODULE_KEYS.has(module.key) && (
                  <div className="mt-3 inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-800 font-medium">
                    {formatPortalModeSummary(module.key, tenant.capabilities_override || {})}
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className={`
                                px-6 py-4 border-t border-gray-100 rounded-b-2xl bg-gray-50/50 flex items-center justify-between
                                ${isEnabled ? 'bg-blue-50/30' : ''}
                            `}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                    {isEnabled ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                {isAvailable && module.key === 'canalpro_enabled' && onConfigureCanalpro && (
                  <button
                    onClick={onConfigureCanalpro}
                    className="text-xs text-blue-600 font-medium flex items-center gap-1 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-1"
                  >
                    Configurar modo <ArrowLeft className="w-3 h-3 rotate-180" />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

    </div>
  )
}
